import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { useProjectId } from 'store/project';
import { useAppNavigation } from 'contexts/navigation';
import { APP_PATHS, isDocumentationPath, isDocumentationRootPath } from 'setup/navigation/paths';
import { useDocumentationStore } from '../../store';
import { ROOT_DIR_ID } from 'shared/constants/directory';
import { isNavigatingOutside } from '../useDocumentationUrlNavigation';
import { resolveStoreDrivenDirectoryUrl } from './model';

export function useDocumentationDirectoryUrlSynchronization(): void {
  const projectId = useProjectId();
  const currentDirectory = useDocumentationStore.use.currentDirectory();
  const galleryOpen = useDocumentationStore.use.galleryOpen();
  const navigateToDirectory = useDocumentationStore.use.navigateToDirectory();
  
  const directoryId = useAppUrlParams(UrlParamName.DIRECTORY_ID);
  const fileId = useAppUrlParams(UrlParamName.FILE_ID);
  const location = useLocation();
  const {
    goToDocumentation,
    goToDocumentationDirectory: goToDocumentationDir,
  } = useAppNavigation();

  const prevCurrentDirectoryRef = useRef(currentDirectory);
  const currentDirectoryRef = useRef(currentDirectory);

  useEffect(() => {
    currentDirectoryRef.current = currentDirectory;
  }, [currentDirectory]);

  useEffect(() => {
    if (fileId) {
      return;
    }

    // Skip while the gallery-close handler is correcting the directory itself
    // (see signalNavigationOutsideDocumentation in useDocumentationUrlNavigation)
    // - otherwise a stale pathname here could bounce that correction back to root.
    if (isNavigatingOutside()) {
      return;
    }

    // Prefer pathname over directoryId so a root navigation is not bounced
    // back to a stale directory param while React Router is still updating.
    if (isDocumentationRootPath(location.pathname)) {
      // Keep the folder under an open gallery. After edit/save the URL can
      // briefly lose fileId / land on root without an intentional root click.
      if (galleryOpen) {
        return;
      }
      if (currentDirectoryRef.current !== ROOT_DIR_ID) {
        navigateToDirectory(ROOT_DIR_ID);
      }
      return;
    }

    if (directoryId) {
      if (directoryId !== currentDirectoryRef.current) {
        navigateToDirectory(directoryId);
      }
    }
  }, [directoryId, fileId, location.pathname, navigateToDirectory, galleryOpen]);

  useEffect(() => {
    const previousStoreDirectory = prevCurrentDirectoryRef.current;
    prevCurrentDirectoryRef.current = currentDirectory;

    // The gallery owns the URL while a file is open.
    if (fileId) {
      return;
    }

    const targetDirectory = resolveStoreDrivenDirectoryUrl({
      previousStoreDirectory,
      storeDirectory: currentDirectory,
      urlDirectoryId: directoryId,
    });
    if (targetDirectory === undefined) {
      return;
    }

    // If user navigated away from documentation section, don't redirect back.
    // Use window.location directly because React state may not be updated yet.
    // Also check the navigation flag for pending navigations.
    if (isNavigatingOutside() || !isDocumentationPath(window.location.pathname)) {
      return;
    }

    if (targetDirectory === ROOT_DIR_ID) {
      if (location.pathname !== APP_PATHS.documentation.root(projectId)) {
        goToDocumentation(projectId, { replace: true });
      }
      return;
    }

    if (location.pathname !== APP_PATHS.documentation.dir(projectId, targetDirectory)) {
      goToDocumentationDir(projectId, targetDirectory, { replace: true });
    }
  }, [
    currentDirectory,
    directoryId,
    fileId,
    location.pathname,
    goToDocumentationDir,
    goToDocumentation,
    projectId,
  ]);

}
